import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import { fetchRfqs } from '../api/marketplaceApi';

const RfqList: React.FC = () => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      const res = await fetchRfqs(params);
      setRfqs(res.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">My RFQs</h2>
        <button onClick={() => navigate('/marketplace/buyer/rfqs/new')}
          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
          <PlusIcon className="w-4 h-4" /> Create RFQ
        </button>
      </div>

      <div className="flex items-center gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
          <option value="">All Status</option>
          <option value="draft">Draft</option><option value="published">Published</option>
          <option value="quotation_open">Quotation Open</option><option value="evaluation">Evaluation</option>
          <option value="awarded">Awarded</option><option value="closed">Closed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" /></div>
      ) : rfqs.length === 0 ? (
        <EmptyState title="No RFQs yet" description="Create a Request for Quotation to start procuring." action={{ label: 'Create RFQ', onClick: () => navigate('/marketplace/buyer/rfqs/new') }} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">RFQ</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Title</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Visibility</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Deadline</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rfqs.map((rfq: any) => (
                <tr key={rfq.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/marketplace/buyer/rfqs/${rfq.id}`)}>
                  <td className="px-4 py-2.5 font-mono text-xs">{rfq.rfq_number}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{rfq.title}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{rfq.visibility?.replace('_', ' ')}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{rfq.quote_deadline ? new Date(rfq.quote_deadline).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={rfq.status} size="sm" /></td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{new Date(rfq.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RfqList;
